import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import { employeeManagementService } from "./employee-management.service";
import { TApiServiceError } from "../apiService";
import { buildQueryParams } from "../../utils/commonUtils";
import {
  TAcceptInvitationForm,
  TConvertToUserRequest,
  TCreateEmployeeForm,
  TCreateInvitationForm,
  TCreateUpdateEmployeePayload,
  TResendInvitationForm,
  TUpdateTenantSettingsForm,
  TEmployeeColumnsResponse,
  TEmployeeChoicesResponse,
  TEmployeeListResponse,
  TBulkEmployeePermissionAssignmentRequest,
  TBulkEmployeePermissionAssignmentResponse,
  TEmployeePermissionToggleVariables,
} from "./employee-management.types";
import {
  TAssignedPermissionListResponse,
  TPermissionListFilters,
} from "../role-management/role-management.types";

// Employee List Query
export const useGetEmployeeListQuery = (
  params: Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TEmployeeListResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) => {
  return useQuery({
    queryKey: ["employee-list", params],
    queryFn: async () => {
      const query = buildQueryParams(params);
      return await employeeManagementService.getEmployeeList(query);
    },
    ...options,
  });
};

// Employee List for Manager Dropdown Query
export const useGetEmployeesForManagerQuery = (options?: {
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ["employees-for-manager"],
    queryFn: () =>
      employeeManagementService.getEmployeeList("?page=1&page_size=100"),
    enabled: options?.enabled ?? true,
  });
};

// Employee Choices Query
export const useGetEmployeeChoicesQuery = (options?: { enabled?: boolean }) => {
  return useQuery<TEmployeeChoicesResponse>({
    queryKey: ["employee-choices"],
    queryFn: () => employeeManagementService.getEmployeeChoices(),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

// Employee Detail Query
export const useGetEmployeeByIdQuery = (
  internalId: string,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey: ["employee-detail", internalId],
    queryFn: () => employeeManagementService.getEmployeeById(internalId),
    enabled: options?.enabled ?? !!internalId,
  });
};

// Create Employee Mutation
export const useCreateEmployeeMutation = (
  options?: Omit<
    UseMutationOptions<any, TApiServiceError, TCreateEmployeeForm>,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: async (data: TCreateEmployeeForm) => {
      return await employeeManagementService.createEmployee(data);
    },
    ...options,
  });
};

// Update Employee Mutation
export const useUpdateEmployeeMutation = (
  options?: Omit<
    UseMutationOptions<any, TApiServiceError, TCreateUpdateEmployeePayload>,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: async (data: TCreateUpdateEmployeePayload) => {
      return await employeeManagementService.updateEmployee(data);
    },
    ...options,
  });
};

// Delete Employee Mutation
export const useDeleteEmployeeMutation = (
  options?: Omit<
    UseMutationOptions<any, TApiServiceError, string>,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: (id: string) => employeeManagementService.deleteEmployee(id),
    ...options,
  });
};

// Bulk Import Preview Mutation
export const useBulkImportPreviewMutation = (
  options?: Omit<
    UseMutationOptions<any, TApiServiceError, FormData>,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: async (data: FormData) => {
      return await employeeManagementService.previewBulkImport(data);
    },
    ...options,
  });
};

// Bulk Upload Mutation
export const useBulkUploadMutation = (
  options?: Omit<
    UseMutationOptions<any, TApiServiceError, FormData>,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: (formData: FormData) =>
      employeeManagementService.uploadEmployeeFile(formData),
    ...options,
  });
};

// Convert Employee to User Mutation
export const useConvertEmployeeToUserMutation = (
  options?: Omit<
    UseMutationOptions<
      any,
      TApiServiceError,
      { employeeId: string; data: TConvertToUserRequest }
    >,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: ({
      employeeId,
      data,
    }: {
      employeeId: string;
      data: TConvertToUserRequest;
    }) => employeeManagementService.convertEmployeeToUser(employeeId, data),
    ...options,
  });
};

// Employee Invitation List Query
export const useGetEmployeeInvitationListQuery = (
  query: string,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey: ["employee-invitation-list", query],
    queryFn: () => employeeManagementService.getEmployeeInvitationList(query),
    enabled: options?.enabled ?? true,
  });
};

// Employee Invitation Detail Query
export const useGetEmployeeInvitationByIdQuery = (
  internalId: string,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey: ["employee-invitation-detail", internalId],
    queryFn: () =>
      employeeManagementService.getEmployeeInvitationById(internalId),
    enabled: options?.enabled ?? !!internalId,
  });
};

// Create Employee Invitation Mutation
export const useCreateEmployeeInvitationMutation = (
  options?: Omit<
    UseMutationOptions<any, TApiServiceError, TCreateInvitationForm>,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: (data: TCreateInvitationForm) =>
      employeeManagementService.createEmployeeInvitation(data),
    ...options,
  });
};

// Resend Employee Invitation Mutation
export const useResendEmployeeInvitationMutation = (
  options?: Omit<
    UseMutationOptions<any, TApiServiceError, TResendInvitationForm>,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: (data: TResendInvitationForm) =>
      employeeManagementService.resendEmployeeInvitation(data),
    ...options,
  });
};

// Cancel Employee Invitation Mutation
export const useCancelEmployeeInvitationMutation = (
  options?: Omit<
    UseMutationOptions<any, TApiServiceError, string>,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: (id: string) =>
      employeeManagementService.cancelEmployeeInvitation(id),
    ...options,
  });
};

// Invitation Stats Query
export const useGetInvitationStatsQuery = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["invitation-stats"],
    queryFn: () => employeeManagementService.getInvitationStats(),
    enabled: options?.enabled ?? true,
  });
};

// Validate Invitation Token Query
export const useValidateInvitationTokenQuery = (
  token: string,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey: ["validate-invitation-token", token],
    queryFn: () => employeeManagementService.validateInvitationToken(token),
    enabled: options?.enabled ?? !!token,
  });
};

// Accept Invitation Mutation
export const useAcceptInvitationMutation = (
  options?: Omit<
    UseMutationOptions<any, TApiServiceError, TAcceptInvitationForm>,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: async (data: TAcceptInvitationForm) => {
      return await employeeManagementService.acceptInvitation(data);
    },
    ...options,
  });
};

// Tenant Settings Query
export const useGetTenantSettingsQuery = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["tenant-employee-settings"],
    queryFn: () => employeeManagementService.getTenantSettings(),
    enabled: options?.enabled ?? true,
  });
};

// Update Tenant Settings Mutation
export const useUpdateTenantSettingsMutation = (
  options?: Omit<
    UseMutationOptions<any, TApiServiceError, TUpdateTenantSettingsForm>,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: (data: TUpdateTenantSettingsForm) =>
      employeeManagementService.updateTenantSettings(data),
    ...options,
  });
};

// Add new query for getting available columns
export const useGetEmployeeColumnsQuery = (params: {
  include_core?: boolean;
  include_attributes?: boolean;
  include_metadata?: boolean;
}) => {
  const queryString = new URLSearchParams();
  if (params.include_core !== undefined) {
    queryString.append("include_core", params.include_core.toString());
  }
  if (params.include_attributes !== undefined) {
    queryString.append(
      "include_attributes",
      params.include_attributes.toString(),
    );
  }
  if (params.include_metadata !== undefined) {
    queryString.append("include_metadata", params.include_metadata.toString());
  }

  return useQuery<TEmployeeColumnsResponse, TApiServiceError>({
    queryKey: ["employee-columns", queryString.toString()],
    queryFn: () =>
      employeeManagementService.getEmployeeColumns(queryString.toString()),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useEmployeeAssignedPermissions = (
  employeeId: string,
  filters: TPermissionListFilters = {},
  options?: Omit<
    UseQueryOptions<TAssignedPermissionListResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  > & {
    enabled?: boolean;
    keepPreviousData: true;
  },
) => {
  return useQuery({
    queryKey: ["employee-assigned-permissions", employeeId, filters],
    queryFn: async () => {
      const query = buildQueryParams(filters as Record<string, unknown>);
      const response =
        await employeeManagementService.getEmployeeAssignedPermissions(
          employeeId,
          query,
        );
      return response.data;
    },
    enabled: (options?.enabled ?? true) && !!employeeId,
    keepPreviousData: options?.keepPreviousData ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useEmployeeAvailablePermissions = (
  employeeId: string,
  filters: TPermissionListFilters = {},
  options?: Omit<
    UseQueryOptions<TAssignedPermissionListResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  > & {
    enabled?: boolean;
    keepPreviousData?: true;
  },
) => {
  return useQuery({
    queryKey: ["employee-available-permissions", employeeId, filters],
    queryFn: async () => {
      const query = buildQueryParams(filters as Record<string, unknown>);
      const response =
        await employeeManagementService.getEmployeeAvailablePermissions(
          employeeId,
          query,
        );
      return response.data;
    },
    enabled: (options?.enabled ?? true) && !!employeeId,
    keepPreviousData: options?.keepPreviousData ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useBulkEmployeePermissionAssignment = (
  options?: Omit<
    UseMutationOptions<
      TBulkEmployeePermissionAssignmentResponse,
      TApiServiceError,
      TBulkEmployeePermissionAssignmentRequest
    >,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: async (data: TBulkEmployeePermissionAssignmentRequest) => {
      const response =
        await employeeManagementService.assignPermissionsToEmployee(data);
      return response.data;
    },
    ...options,
  });
};

export const useEmployeePermissionToggle = (
  options?: Omit<
    UseMutationOptions<
      TBulkEmployeePermissionAssignmentResponse,
      TApiServiceError,
      TEmployeePermissionToggleVariables
    >,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: async ({
      employeeId,
      permissionId,
      isAssigned,
    }: TEmployeePermissionToggleVariables) => {
      const response =
        await employeeManagementService.assignPermissionsToEmployee({
          employee_id: employeeId,
          permission_ids: [permissionId],
          action: isAssigned ? "remove" : "assign",
        });
      return response.data;
    },
    ...options,
  });
};
